function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step)
        && this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    if (
      this.input.dataset.min === previousValue
      && event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        'disabled',
        parseInt(value) <= parseInt(this.input.min),
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class DeliveryDatePicker extends HTMLElement {
  constructor() {
    super();
    this.datePickerInput = this.querySelector('[id="DeliveryDate-DatePicker"]');
    this.datePickerContainer = this
      .querySelector('[id="DeliveryDate-DatePickerContainer"]');
    this.nonMainlandInput = this
      .querySelector('[id="DeliveryDate-NonMainland"]');
    this.collectionInput = this
      .querySelector('[id="DeliveryDate-Collection"]');
    this.collectionMessage = this
      .querySelector('[id="DeliveryDate-CollectionMessage"]');

    if (!this.datePickerInput || !Datepicker) {
      this.style.display = 'none';
      return;
    }

    this.deliveryDays = [2, 3, 4, 5];
    this.shippingDays = [1, 2, 3, 4, 5];
    this.cutoffHour = 13;
    this.cutoffMinute = 0;
    this.dayOffset = 1;
    // Christmas pre-order rules are enabled by the cart's Liquid data attribute.
    this.christmasPreorder = this.dataset.christmasPreorder === 'true';
    this.christmasMinimumDate = new Date(2026, 10, 23);
    this.nextDeliveryDate = this.getNextDeliveryDate();
    this.maxDate = this.getMaxDate();
    this.disabledDates = [
  new Date(2026, 0, 1),   // New Year's Day
  new Date(2026, 3, 3),   // Good Friday
  new Date(2026, 3, 6),   // Easter Monday
  new Date(2026, 3, 7),   // Easter Tuesday
  new Date(2026, 4, 4),   // Early May Bank Holiday
  new Date(2026, 4, 5),   // Early May Bank Holiday
  new Date(2026, 4, 25),  // Spring Bank Holiday
  new Date(2026, 4, 26),  // Spring Bank Holiday
  new Date(2026, 7, 31),  // Summer Bank Holiday
  new Date(2026, 8, 1),   // Summer Bank Holiday
  new Date(2026, 8, 2),   // Summer Bank Holiday
  new Date(2026, 11, 25), // Christmas Day
  new Date(2026, 11, 26), // Boxing Day
  new Date(2026, 11, 29), // Tuesday 29 December
  new Date(2027, 0, 1)    // New Year's Day 2027
]

    this.init();
  }

  getNextDeliveryDate(customOffset = 0) {
    const today = new Date();
    const isBeforeCutoff = this.shippingDays.includes(today.getDay()) && today.getHours() < this.cutoffHour;
    const deliveryDate = new Date(today);
    let offset = 1 + customOffset;

    if (!isBeforeCutoff) {
      offset++;
    }

    while (!this.deliveryDays.includes((deliveryDate.getDay() + offset) % 7)) {
      offset++;
    }

    deliveryDate.setDate(deliveryDate.getDate() + offset);
    return deliveryDate;
  }

  getMinimumDeliveryDate(extraDay = 0) {
    const normalDate = this.getNextDeliveryDate(extraDay);
    if (this.christmasPreorder && normalDate < this.christmasMinimumDate) {
      return new Date(this.christmasMinimumDate);
    }
    return normalDate;
  }

  clearInvalidChristmasDate(selectedValue) {
    if (!this.christmasPreorder || !selectedValue?.trim()) return;
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(selectedValue.trim());
    let allowed = false;
    if (match) {
      const chosenDate = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
      const maxMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(this.maxDate);
      const latest = maxMatch && new Date(Number(maxMatch[3]), Number(maxMatch[2]) - 1, Number(maxMatch[1]));
      allowed = chosenDate.getDate() === Number(match[1]) &&
        chosenDate.getMonth() === Number(match[2]) - 1 &&
        chosenDate.getFullYear() === Number(match[3]) &&
        chosenDate >= this.getMinimumDeliveryDate(this.nonMainlandInput?.checked ? 1 : 0) &&
        (!latest || chosenDate <= latest) &&
        this.deliveryDays.includes(chosenDate.getDay()) &&
        !this.disabledDates.some(holiday => holiday.toDateString() === chosenDate.toDateString());
    }
    if (allowed) return;
    this.datePickerInput.datepicker.setDate({ clear: true });
    this.datePickerInput.value = '';
    this.dispatchCartRequest(null, '');
  }

  init() {
    // Preserve the cart attribute before Datepicker possibly normalises an out-of-range input.
    const selectedBeforeInit = this.datePickerInput.value;
    this.datepicker = new Datepicker(
      this.datePickerInput,
      {
        beforeShowDay: (date) => {
          return {
            enabled: !this.disabledDates.some(holiday =>
              date.getFullYear() === holiday.getFullYear() &&
              date.getMonth() === holiday.getMonth() &&
              date.getDate() === holiday.getDate()
            )
          };
        },
        clearButton: false,
        daysOfWeekDisabled: [0, 1, 6],
        format: 'dd/mm/yyyy',
        language: 'en-GB',
        maxDate: this.maxDate,
        maxNumberOfDates: 1,
        minDate: this.getMinimumDeliveryDate(),
        todayHighlight: true,
        updateOnBlur: true,
      },
    );

    this.clearInvalidChristmasDate(selectedBeforeInit);

    this.datePickerInput.addEventListener(
      'changeDate',
      this.handleChange.bind(this),
    );

    // A manually typed invalid date may never emit changeDate.
    this.datePickerInput.addEventListener('change', () => {
      this.clearInvalidChristmasDate(this.datePickerInput.value);
    });

    this.nonMainlandInput.addEventListener(
      'change',
      this.handleNonMainlandUpdate.bind(this),
    );

    this.collectionInput.addEventListener(
      'change',
      this.handleCollectionUpdate.bind(this),
    );
  }

  getMaxDate() {
    const today = new Date();
    const maxTime = today.getTime() + 42 * 24 * 60 * 60 * 1000;
    const maxDate = new Date(maxTime);

    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    return formatter.format(maxDate);
  }

  handleCollectionUpdate(event) {
    if (event.currentTarget.checked) {
      this.dispatchCartRequest(event);
      this.datePickerInput.datepicker.setDate({ clear: true });
      this.datePickerContainer.classList.add('visually-hidden');
      this.collectionMessage.classList.remove('visually-hidden');
    } else {
      this.datePickerContainer.classList.remove('visually-hidden');
      this.collectionMessage.classList.add('visually-hidden');
    }
  }

  handleNonMainlandUpdate(event) {
    if (event.currentTarget.checked) {
      this.datePickerInput.datepicker.setDate({ clear: true });
      this.datePickerInput.datepicker.setOptions({
        beforeShowDay: (date) => {
          return {
            enabled: !this.disabledDates.some(holiday =>
              date.getFullYear() === holiday.getFullYear() &&
              date.getMonth() === holiday.getMonth() &&
              date.getDate() === holiday.getDate()
            )
          };
        },
        clearButton: false,
        daysOfWeekDisabled: [0, 1, 6],
        format: 'dd/mm/yyyy',
        language: 'en-GB',
        maxDate: this.maxDate,
        maxNumberOfDates: 1,
        minDate: this.getMinimumDeliveryDate(1),
        todayHighlight: true,
        updateOnBlur: true,
      })
    } else {
      this.datePickerInput.datepicker.setOptions({
        beforeShowDay: (date) => {
          return {
            enabled: !this.disabledDates.some(holiday =>
              date.getFullYear() === holiday.getFullYear() &&
              date.getMonth() === holiday.getMonth() &&
              date.getDate() === holiday.getDate()
            )
          };
        },
        clearButton: false,
        daysOfWeekDisabled: [0, 1, 6],
        format: 'dd/mm/yyyy',
        language: 'en-GB',
        maxDate: this.maxDate,
        maxNumberOfDates: 1,
        minDate: this.getMinimumDeliveryDate(),
        todayHighlight: true,
        updateOnBlur: true,
      })
    }
  }

  handleChange(event) {
    this.dispatchCartRequest(event, event.target.value);
    document.dispatchEvent(
      new CustomEvent('datepicker:set', { detail: event.detail }),
    );
    this.datePickerInput.datepicker.hide();
  }

  dispatchCartRequest(event, deliveryDate = '') {
    const body = JSON.stringify(
      { attributes: { 'Delivery date': deliveryDate } }
    );

    fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      ...{ body },
    })
      .then(() => {
        if (event && typeof CartPerformance !== 'undefined') {
          CartPerformance.measureFromEvent('delivery-date-update:user-action', event);
        }
      });
  }
}

customElements.define('delivery-date-picker', DeliveryDatePicker);

class MealPlan extends HTMLElement {
  constructor() {
    super();

    this.responseData = {

    }

    this.form = this.querySelector('form');

    this.stepElements = this.querySelectorAll('[id^=Step-]');
    this.currentStep = 1;
    this.currentOffset = 0

    this.maxQuestion = 1;

    this.questionElements = this.querySelectorAll('.form-group');
    this.currentQuestion = 1;

    this.track = this.querySelector('[data-track]');
    this.nextButton = this.querySelector('[id="MealPlan-Next"]');
    this.prevButton = this.querySelector('[id="MealPlan-Prev"]');
    this.submitButton = this.querySelector('[id="MealPlan-Submit"]');

    if (!this.questionElements.length) {
      return;
    }

    this.init();

    const resizeObserver = new ResizeObserver((_) => this.getQuestionWidth());
    resizeObserver.observe(this.form);
  }

  init() {
    this.getQuestionWidth();
    this.form.addEventListener('input', this.handleFormChange.bind(this));
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.nextButton.addEventListener('click', this.handleClickControl.bind(this));
    this.prevButton.addEventListener('click', this.handleClickControl.bind(this));
    this.addEventListener('question:move', this.handleToggleButtons.bind(this));
    this.addEventListener('question:complete', this.handleQuestionComplete.bind(this));
    this.addEventListener('question:incomplete', this.handleQuestionIncomplete.bind(this));
  }

  handleFormChange(event) {
    const formData = new FormData(this.form);
    const detailData = {
      detail: {
        input: event.target.name,
        index: Number(event.target.closest('.form-group')?.dataset.index || 1),
      }
    }

    const questionCompleteEvent = new CustomEvent('question:complete', detailData);
    const questionIncompleteEvent = new CustomEvent('question:incomplete', detailData);

    if (
      event.target.name === 'petAgeYears'
      || event.target.name === 'petAgeMonths'
    ) {
      const ageYears = formData.get('petAgeYears');
      const ageMonths = formData.get('petAgeMonths');
      if (ageYears >= 30) {
        formData.set('petAgeYears', 30);
        this.querySelector('[name="petAgeYears"]').value = 30;
      }

      if (ageMonths >= 12) {
        formData.set('petAgeMonths', 12);
        this.querySelector('[name="petAgeMonths"]').value = 12;
      }
      if ((ageYears && ageYears >= 0) && (ageMonths && ageMonths >= 0)) {
        this.dispatchEvent(questionCompleteEvent)
      } else {
        this.dispatchEvent(questionIncompleteEvent);
      }
    }

    if (event.target.name === 'petWeight') {
      const petWeight = formData.get('petWeight');
      if (petWeight && petWeight >= 0) {
        this.dispatchEvent(questionCompleteEvent);
      } else {
        this.dispatchEvent(questionIncompleteEvent);
      }
    }

    if (event.target.name === 'petBrands') {
      const petBrands = formData.get('petBrands');
      if (petBrands.length <= 0) {
        this.dispatchEvent(questionIncompleteEvent);
      } else {
        this.dispatchEvent(questionCompleteEvent);
      }
    }

    if (event.target.name === 'petEmail') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const petEmail = formData.get('petEmail');
      if (!emailRegex.test(petEmail)) {
        this.dispatchEvent(questionIncompleteEvent);
      } else {
        this.dispatchEvent(questionCompleteEvent);
      }
    }

    if (['petType', 'petGender', 'petActivity', 'petBreed', 'petDiet', 'petAilments', 'petSymptoms', 'petPreviouslyOrdered'].includes(event.target.name)) {
      this.dispatchEvent(questionCompleteEvent)
    }
  }

  handleSubmit() {

  }

  handleQuestionComplete(event) {
    const index = event.detail.index;
    if (this.currentQuestion === index) {
      this.nextButton.removeAttribute('disabled');
    }

    this.maxQuestion = index + 1;
    this.handleToggleButtons();
  }

  handleQuestionIncomplete(_event) {
    if (this.maxQuestion > this.currentQuestion) {
      this.maxQuestion = this.currentQuestion;
    }
    this.handleToggleButtons();
  }

  handleToggleButtons() {
    const isLastQuestion = this.currentQuestion === this.questionElements.length;
    if (isLastQuestion || this.maxQuestion <= this.currentQuestion) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else if (this.maxQuestion > this.currentQuestion) {
      this.nextButton.removeAttribute('disabled');
    }

    this.nextButton.classList.toggle('hidden', isLastQuestion);
    this.submitButton.classList.toggle('hidden', !isLastQuestion);

    if (isLastQuestion && this.maxQuestion > this.questionElements.length) {
      this.submitButton.removeAttribute('disabled')
    } else {
      this.submitButton.setAttribute('disabled', 'disabled');
    }

    if (this.currentQuestion > 1) {
      this.prevButton.removeAttribute('disabled');
    } else {
      this.prevButton.setAttribute('disabled', 'disabled');
    }
  }

  disableControls() {
    this.nextButton.setAttribute('disabled', 'disabled');
    this.prevButton.setAttribute('disabled', 'disabled');
  }

  enableControls(timer = 500) {
    setTimeout(() => {
      this.handleToggleButtons();
    }, timer);
  }

  move(direction = '>') {
    this.disableControls();
    this.currentQuestion = direction === '>'
      ? this.currentQuestion + 1
      : this.currentQuestion - 1;

    this.currentOffset = this
      .questionElements[this.currentQuestion - 1]
      .offsetLeft
      * -1;

    this.track.style.transform = `translateX(${this.currentOffset}px)`;
    this.dispatchEvent(new CustomEvent('question:move', {
      detail: {
        direction,
        currentQuestion: this.currentQuestion,
      }
    }));

    this.enableControls();
  }

  handleClickControl(event) {
    event.preventDefault();
    const direction = event.currentTarget.dataset.value || '>';
    if (
      event.currentTarget.disabled
      || (direction === '>' && this.currentQuestion >= this.questionElements.length)
      || (direction === '<' && this.currentQuestion <= 1)
    ) {
      return;
    }

    this.move(direction);
  }

  getQuestionWidth() {
    this.questionWidth = this.questionElements[0].getBoundingClientRect().width;
  }

  setOffset(spacing = 30) {
    this.currentOffset = (((this.currentQuestion - 1) * this.questionWidth) + spacing) * -1;
  }
}

customElements.define('meal-plan', MealPlan);

class PortionCalculator extends HTMLElement {
  static ACTIVITY_ENUM = {
    LOW: 'low',
    MID: 'mid',
    HIGH: 'high',
  };

  constructor() {
    super();

    this.form = this.querySelector('form');
    this.stepElements = this.querySelectorAll('[id^=Step-]');
    this.currentStep = 0;
    this.quantityResult = 0;
    this.dailyAmountResult = 0;

    this.dailyLowerAmountElement = this.querySelector('[id^="PortionCalculatorDailyLower-"]');
    this.dailyHigherAmountElement = this.querySelector('[id^="PortionCalculatorDailyHigher-"]');

    this.defaultSelection = this.dataset.default || 'none';

    this.dataScript = this.querySelector('[id^=PortionCalculatorData-]');
    this.jsonData = JSON.parse(this.dataScript.innerHTML);

    this.linkElement = this.querySelector('[id^=PortionCalculatorLink-]');
    this.pattiesElement = this.querySelector('[id^=PortionCalculatorPattiesQty-]');

    this.yearsInput = this.form.querySelector('input[type="number"][name="ageYears"]');
    this.monthsInput = this.form.querySelector('input[type="number"][name="ageMonths"]');

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.form.addEventListener('reset', this.handleReset.bind(this));

    this.yearsInput.addEventListener('change', this.handleClearAgeValidity.bind(this));
    this.monthsInput.addEventListener('change', this.handleClearAgeValidity.bind(this));

    const resizeObserver = new ResizeObserver((_) => this.initForm());
    resizeObserver.observe(this.form);
    this.initForm();
  }

  initForm() {
    this.form.style.minHeight = this.stepElements[this.currentStep].getBoundingClientRect().height + 40 + 'px';
  }

  handleClearAgeValidity() {
    this.yearsInput.setCustomValidity('');
    this.monthsInput.setCustomValidity('');
  }

  handleReset() {
    if (this.currentStep >= 0) {
      this.stepElements[this.currentStep].classList.add('form-multistep__step--hidden');
    }

    if (this.stepElements[0].classList.contains('form-multistep__step--hidden')) {
      setTimeout(() => {
        this.stepElements[0].classList.remove('form-multistep__step--hidden');
        this.form.style.minHeight = this.stepElements[0].getBoundingClientRect().height + 40 + 'px';
        this.currentStep = 0;
        this.pattiesElement.innerText = '5-7';
      }, 500);
    }
  }

  /**
   * @desc Calculates the quantity required for the given pet.
   */
static calculateQuantity({
  activity,
  ageYears,
  ageMonths,
  bodyCondition,
  weight,
  petWeightUnit,
}) {

  // DEFAULT VALUES ADDED
  activity = activity || 'mid';
  bodyCondition = bodyCondition || 'average';

  // Convert weight to kilograms before calculating
  weight = Number(weight);

  if (petWeightUnit === 'lb') {
    weight = weight / 2.2046226218;
  }

    if (isNaN(weight) || isNaN(ageYears)) {
      throw new Error('Cannot calculate quantity - invalid data passed');
    }

    let feedingPercent = 0.025;

    if (ageYears <= 0) {
      if (ageMonths < 4) {
        feedingPercent = 0.07;
      } else if (ageMonths <= 6) {
        feedingPercent = 0.06;
      } else if (ageMonths <= 8) {
        feedingPercent = 0.055;
      } else if (ageMonths <= 11) {
        feedingPercent = 0.05;
      }
    }

    let bodyConditionPercent = 1;
    switch (bodyCondition) {
      case 'slim':
        bodyConditionPercent = 1.05;
        break;
      case 'large':
        bodyConditionPercent = 0.95;
        break;
      case 'extra large':
        bodyConditionPercent = 0.9;
        break;
    }

    let activityPercent = 1;
    if (activity === 'low') {
      activityPercent = 0.95;
    } else if (activity === 'high') {
      activityPercent = 1.05;
    }

    const portionGrams = weight * feedingPercent * bodyConditionPercent * activityPercent * 1000;
    const pattiesPerDay = Math.ceil(portionGrams / 100);

    const portionDisplay = pattiesPerDay * 100;
    const daysPerBox = Math.floor(72 / pattiesPerDay);

    return { daysPerBox, pattiesPerDay, portionDisplay, portionGrams };
  }

  handleSubmit(event) {
    event.preventDefault();

    if (this.yearsInput && this.monthsInput) {
      if (this.yearsInput.value <= 0 && this.monthsInput.value <= 0) {
        this.yearsInput.setCustomValidity('At least one of "Years" or "Months" must be greater than 0');
        this.monthsInput.setCustomValidity('At least one of "Years" or "Months" must be greater than 0');
        this.yearsInput.reportValidity();
        this.monthsInput.reportValidity();
        return;
      }
    }

    const offsetPosition = this.getBoundingClientRect().top + window.pageYOffset - 80;

    window.scrollTo({
      behavior: 'smooth',
      top: offsetPosition,
    });

    const formData = new FormData(event.currentTarget);
    const jsonData = Object.fromEntries(formData.entries());

    // ADD DEFAULT VALUES BEFORE CALCULATION
    jsonData.activity = jsonData.activity || 'mid';
    jsonData.bodyCondition = jsonData.bodyCondition || 'average';

    const { pattiesPerDay } = PortionCalculator.calculateQuantity(jsonData);

    this.pattiesElement.innerText = pattiesPerDay;

    const perMealAmount = pattiesPerDay / 2;
    const perMealText = perMealAmount % 1 === 0 ? perMealAmount.toString() : perMealAmount.toFixed(1);

    this.dailyLowerAmountElement.innerText = perMealText;
    this.dailyHigherAmountElement.innerText = perMealText;

    this.stepElements[this.currentStep].classList.add('form-multistep__step--hidden');
    setTimeout(() => {
      this.currentStep++;
      this.stepElements[this.currentStep].classList.remove('form-multistep__step--hidden');
      this.form.style.minHeight = this.stepElements[this.currentStep].getBoundingClientRect().height + 40 + 'px';
    }, 500);
  }
}

customElements.define('portion-calculator', PortionCalculator);

class ProductDescription extends HTMLElement {
  constructor() {
    super();
    if (this.classList.contains('product__description--no-clamp')) {
      return;
    }

    this.wrapperElement = this.querySelector('.product__description-wrapper');

    this.init();
  }

  init() {
    if (this.wrapperElement.scrollHeight > this.wrapperElement.clientHeight) {
      const button = document.createElement('button')
      button.type = 'button';
      button.textContent = 'Read more';
      button.classList.add('button__read-more');
      this.appendChild(button);
      button.addEventListener('click', () => {
        if (this.wrapperElement.style.display === '-webkit-box' || !this.wrapperElement.style.display) {
          this.wrapperElement.style.display = 'block';
          button.textContent = 'Show less';
        } else {
          this.wrapperElement.style.display = '-webkit-box';
          button.textContent = 'Read more';
        }
      });
    }
  }
}

customElements.define('product-description', ProductDescription);

class DeliveryTimer extends HTMLElement {
  constructor() {
    super();

    this.textContainerElement = this.querySelector('p');
    this.timerElement = this.querySelector('p>span');
    this.dateElement = this.querySelector('p>strong');

    this.deliveryDays = [2, 3, 4, 5];
    this.shippingDays = [1, 2, 3, 4, 5];
    this.cutoffHour = 13;
    this.cutoffMinute = 0;

    setInterval(this.updateDeliveryMessage.bind(this), 1000);
  }

  getNextDeliveryDate() {
    const today = new Date();
    const deliveryDate = new Date(today);
    let dayOffset = 1;
    const isBeforeCutoff = today.getHours() < this.cutoffHour
      || (
        today.getHours() === this.cutoffHour
        && today.getMinutes() < this.cutoffMinute
      );

    if (!isBeforeCutoff) {
      dayOffset++
    }

    while (
      !this.deliveryDays.includes((deliveryDate.getDay() + dayOffset) % 7)
    ) {
      dayOffset++;
    }

    deliveryDate.setDate(today.getDate() + dayOffset);

    return deliveryDate;
  }

  updateDeliveryMessage() {
    const now = new Date();
    const nextDeliveryDate = this.getNextDeliveryDate();
    const todayIsShippingDay = this.shippingDays.includes(now.getDay());
    const isBeforeCutoff = now.getHours() < this.cutoffHour
      || (
        now.getHours() === this.cutoffHour
        && now.getMinutes() < this.cutoffMinute
      );

    if (todayIsShippingDay && isBeforeCutoff) {
      this.timerElement.textContent = this.formatTimeRemaining()
    } else {
      this.timerElement.textContent = 'now';
    }

    this.dateElement.textContent = this.formatDate(nextDeliveryDate);
  }

  formatTimeRemaining() {
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);

    const diff = cutoff - now;
    let seconds = Math.floor((diff / 1000) % 60);
    let minutes = Math.floor((diff / 1000) / 60) % 60;
    const hours = Math.floor((diff / 1000) / 3600);

    return `within ${hours}h ${minutes}m ${seconds}s`;
  }

  formatDate(date) {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('en-GB', options);
  }
}

customElements.define('delivery-timer', DeliveryTimer);

class ProductCollapsibleContent extends HTMLElement {
  constructor() {
    super();
    this.collapsibleItems = this.querySelectorAll('.accordion');
    if (!this.collapsibleItems.length) {
      return;
    }

    this.maxHeight = 0;
    this.summaryHeight = this.setSummaryHeight();
    this.collapsibleItems.forEach((item) => item.addEventListener('click', this.handleOpenItem.bind(this)));
    this.wrapper = this.querySelector('.collapsible-content__grid-item');
    this.openItem = this.querySelector('.accordion>details[open]');
    this.init();
  }

  setSummaryHeight() {
    this.collapsibleItems.forEach((item) => {
      const summary = item.querySelector('summary');
      if (summary.getBoundingClientRect().height > this.maxHeight) {
        this.maxHeight = summary.getBoundingClientRect().height;
      }
    })

    this.collapsibleItems.forEach((item) => {
      const summary = item.querySelector('summary');
      const content = item.querySelector('.accordion__content');
      summary.style.height = this.maxHeight + 'px';
      content.style.top = this.maxHeight + 'px';
    })
  }

  init() {
    if (this.openItem) {
      this.openItem.style.height = this.getContainerHeight(this.openItem) + 'px';
    }
  }

  getContainerHeight(item) {
    const summaryElement = item.querySelector('summary');
    const contentElement = item.querySelector('.accordion__content');
    if (!summaryElement || !contentElement) {
      return 0;
    }

    return contentElement.getBoundingClientRect().height + summaryElement.getBoundingClientRect().height;
  }

  getSummaryHeight(item) {
    const summaryElement = item.querySelector('summary');
    if (!summaryElement) {
      return 0;
    }

    return summaryElement.getBoundingClientRect().height;
  }

  handleOpenItem(event) {
    event.preventDefault();
    const item = event.currentTarget;
    const itemDetails = item.querySelector('details');

    if (!itemDetails || itemDetails === this.openItem) {
      return;
    }

    setTimeout(() => {
      this.openItem.removeAttribute('open');
      this.openItem.classList.remove('details--open');
      this.openItem.style.height = this.getSummaryHeight(this.openItem) + 'px';

      this.openItem = itemDetails;
      this.openItem.setAttribute('open', 'open');
      this.openItem.classList.add('details--open');
      this.openItem.style.height = this.getContainerHeight(this.openItem) + 'px';
    }, 100)
  }
}

customElements.define('product-collapsible-content', ProductCollapsibleContent);

class CollapsibleContent extends HTMLElement {
  constructor() {
    super();

    if (window.innerWidth < 989 || !this.classList.contains('row')) {
      return;
    }

    this.collapsibleItems = this.querySelectorAll('.accordion');
    if (!this.collapsibleItems.length) {
      return;
    }

    this.collapsibleItems.forEach((item) => item.addEventListener('click', this.handleOpenItem.bind(this)));

    this.openItem = this.querySelector('.accordion>details[open]');
    this.init();
  }

  init() {
    if (this.openItem) {
      this.openItem.style.minHeight = this.getContainerHeight(this.openItem) + 'px';
    }
  }

  getContainerHeight(item) {
    const summaryElement = item.querySelector('summary');
    const contentElement = item.querySelector('.accordion__content');
    if (!summaryElement || !contentElement) {
      return 0;
    }

    const calcHeight = contentElement.getBoundingClientRect().height + summaryElement.getBoundingClientRect().height;
    return calcHeight > 2500 ? 2500 : calcHeight;
  }

  handleOpenItem(event) {
    event.preventDefault();
    const item = event.currentTarget;
    const itemDetails = item.querySelector('details');

    if (!itemDetails || itemDetails === this.openItem) {
      return;
    }

    this.openItem.classList.remove('details--open');

    setTimeout(() => {
      this.openItem.removeAttribute('open');
      itemDetails.classList.add('details--open');
      itemDetails.setAttribute('open', 'open');
      this.openItem.style.minHeight = this.getContainerHeight(itemDetails) + 'px';

      this.openItem = itemDetails;
      this.openItem.style.minHeight = this.getContainerHeight(itemDetails) + 'px';
    }, 100)
  }
}

customElements.define('collapsible-content', CollapsibleContent);

class GlideComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^=Glide-].glide--enabled');
    if (!this.slider) {
      return;
    }

    this.initialSlides = this.querySelectorAll('li.glide__slide');
    this.totalSlides = Number(this.dataset.slides) || this.initialSlides.length;
    this.track = this.querySelector('.glide__track');
    this.controls = this.querySelector('[data-glide-el="controls"]');

    this.settings = {
      gap: {
        mobile: Number(this.dataset.mobileGap || 0),
        tablet: Number(this.dataset.tabletGap || 0),
        desktop: Number(this.dataset.desktopGap || 0),
      },
      peek: {
        mobile: Number(this.dataset.mobilePeek || 0),
        tablet: Number(this.dataset.tabletPeek || 0),
        desktop: Number(this.dataset.desktopPeek || 0),
      },
      perView: {
        mobile: Number(this.dataset.mobilePerView || 0),
        tablet: Number(this.dataset.tabletPerView || 0),
        desktop: Number(this.dataset.desktopPerView || 0),
      },
      type: this.dataset.type || 'carousel',
    }

    this.init();
  }

  init() {
    this.glide = new Glide(this.slider, {
      breakpoints: {
        749: {
          gap: this.settings.gap.mobile,
          peek: this.settings.peek.mobile,
          perView: this.settings.perView.mobile,
        },
        989: {
          gap: this.settings.gap.tablet,
          peek: this.settings.peek.tablet,
          perView: this.settings.perView.tablet,
        }
      },
      gap: this.settings.gap.desktop,
      peek: this.settings.peek.desktop,
      perView: this.settings.perView.desktop,
      type: this.settings.type,
    });

    if (
      (window.innerWidth > 989 && this.totalSlides <= this.settings.perView.desktop)
      || (window.innerWidth > 749 && this.totalSlides <= this.settings.perView.tablet)
      || this.totalSlides <= this.settings.perView.mobile
    ) {
      this.controls.style.display = 'none';
      this.initialSlides.forEach((slide) => slide.classList.add('glide__slide--visible'));
      return;
    }

    this.glide.on('mount.before', this.handlePreMount.bind(this))
    this.glide.on(['run', 'run.after', 'mount.after'], this.update.bind(this));
    this.glide.mount();

    const resizeObserver = new ResizeObserver(() => this.update());
    resizeObserver.observe(this.slider);
  }

  handlePreMount() {
    if (!this.track.classList.contains('glide-loaded')) {
      this.track.classList.remove('mx-auto', 'page-width');
      this.track.querySelector('ul').classList.remove('grid');
      this.track.querySelectorAll('li').forEach((li) => li.classList.remove('grid__item'));
    }
  }

  handleDisable() {
    this.track.classList.add('mx-auto', 'page-width');
    this.track.querySelector('ul').classList.add('grid');
    this.track.querySelectorAll('li').forEach((li) => li.classList.add('grid__item'));
  }

  update() {
    if (!this.track.classList.contains('glide-loaded')) {
      this.track.classList.add('glide-loaded');
    }

    // if (this.glide.settings.perView >= this.totalSlides) {
    //   this.glide.disable();
    //   return;
    // } else {
    //   this.glide.enable();
    // }

    const perView = this.glide.settings.perView;
    const slides = this.querySelectorAll('.glide__slide');
    slides.forEach((s) => s.classList.remove('glide__slide--visible'))

    const activeElementIndex = Array
      .from(slides)
      .findIndex((slide) => slide.classList.contains('glide__slide--active'));

    for (let i = activeElementIndex; i < activeElementIndex + perView; i++) {
      slides[i].classList.add('glide__slide--visible');
    }
  }
}

customElements.define('glide-component', GlideComponent);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    this.sliderItemsToShow.forEach((item, index) => {
      if (this.currentPage === index + 1) {
        item.classList.remove('slider__slide--inactive');
      } else {
        item.classList.add('slider__slide--inactive');
      }
    })

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
    this.loadRecommendations(this.dataset.productId);
  }


  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        const glideComponent = html.querySelector('glide-component');

        if (recommendations?.innerHTML.trim().length) {
          if (glideComponent) {
            glideComponent.querySelector('.glide')?.classList.add('glide--enabled');
          }
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      document.querySelector(this.getAttribute('href')).scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    })
  })
})

document.addEventListener("DOMContentLoaded", function () {
  // Toggle open-close functionality for main sections
  document.querySelectorAll("h3.open-close").forEach(function (element) {
    element.addEventListener("click", function () {
      // Check if the element already has the "current" class
      if (this.classList.contains("current")) {
        this.classList.remove("current");
        this.nextElementSibling.style.display = "none"; // Slide up
      } else {
        // Hide all .desc elements and remove "current" class from other h3s
        document.querySelectorAll(".desc").forEach(function (desc) {
          desc.style.display = "none";
        });
        document.querySelectorAll("h3.open-close").forEach(function (h3) {
          h3.classList.remove("current");
        });

        // Show the selected .desc element and add "current" class
        this.classList.add("current");
        this.nextElementSibling.style.display = "block"; // Slide down
      }
    });
  });

  // Initially hide all .sub-desc elements
  document.querySelectorAll(".sub-desc").forEach(function (subDesc) {
    subDesc.style.display = "none";
  });

  // Toggle open-close functionality for sub-sections
  document.querySelectorAll("h3.sub-open-close").forEach(function (element) {
    element.addEventListener("click", function () {
      // Check if the element already has the "sub-current" class
      if (this.classList.contains("sub-current")) {
        this.classList.remove("sub-current");
        this.nextElementSibling.style.display = "none"; // Slide up
      } else {
        // Hide all .sub-desc elements and remove "sub-current" class from other h3s
        document.querySelectorAll(".sub-desc").forEach(function (subDesc) {
          subDesc.style.display = "none";
        });
        document.querySelectorAll("h3.sub-open-close").forEach(function (h3) {
          h3.classList.remove("sub-current");
        });

        // Show the selected .sub-desc element and add "sub-current" class
        this.classList.add("sub-current");
        this.nextElementSibling.style.display = "block"; // Slide down
      }
    });
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const copyLinks = document.querySelectorAll('[data-copy="click"]');
  copyLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const textContent = link.innerText;

      navigator.clipboard
        .writeText(textContent)
        .then(() => {
          link.textContent = "Copied!";
          setTimeout(() => {
            link.textContent = textContent;
          }, 1500);
        }).catch(err => console.error("Failed to copy text: ", err));
    })
  })
})